import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Trash2,
  Save,
  Loader2,
  BadgeInfo,
  CheckCircle2,
  Edit3,
  Pencil,
  Sparkles,
  GitCompareArrows,
  History,
  X,
  ChevronDown,
  Search,
} from "lucide-react";
import {
  getExamination,
  deleteExamination,
  patchExamination,
  verifyExamination,
  downloadPdf,
  getPriorExaminations,
  listExaminations,
} from "@/lib/api";
import InteractiveBoxEditor from "@/components/InteractiveBoxEditor";
import ReportDraftPanel from "@/components/ReportDraftPanel";
import ILOPanel from "@/components/ILOPanel";
import { HardHat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useClasses } from "@/contexts/ClassesContext";

export default function ExamDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { classNames: CLASSES, classColors: CLASS_COLORS } = useClasses();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [radNotes, setRadNotes] = useState("");
  const [finalDiag, setFinalDiag] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [priorList, setPriorList] = useState([]);
  const [compareId, setCompareId] = useState(searchParams.get("compare") || "");
  const [compareExam, setCompareExam] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reloadExam = async () => {
    const updated = await getExamination(id);
    setExam(updated);
    return updated;
  };

  useEffect(() => {
    getExamination(id)
      .then((d) => {
        setExam(d);
        setRadNotes(d.radiologist_notes || "");
        setFinalDiag(d.final_diagnosis || "");
        const initial =
          d.verification_status === "corrected" && d.corrections?.length
            ? d.corrections
            : (d.predictions || []).map((p) => ({ ...p, source: "ai" }));
        setBoxes(initial);
      })
      .catch(() => toast.error("Gagal memuat pemeriksaan"))
      .finally(() => setLoading(false));
  }, [id]);

  // Load prior examinations for the same patient
  useEffect(() => {
    if (!id) return;
    getPriorExaminations(id)
      .then((d) => {
        setPriorList(d.prior || []);
        // Auto-select most recent prior IF user didn't pick one via URL param
        if ((d.prior || []).length > 0 && !searchParams.get("compare")) {
          const autoId = d.prior[0].id;
          setCompareId(autoId);
          // Reflect in URL (replace so no history clutter)
          setSearchParams({ compare: autoId }, { replace: true });
        }
      })
      .catch(() => {});
  }, [id]);

  // Load the comparison exam when compareId changes
  useEffect(() => {
    if (!compareId) {
      setCompareExam(null);
      return;
    }
    setCompareLoading(true);
    getExamination(compareId)
      .then(setCompareExam)
      .catch(() => setCompareExam(null))
      .finally(() => setCompareLoading(false));
  }, [compareId]);

  const changeCompare = (newId) => {
    setCompareId(newId);
    if (newId) setSearchParams({ compare: newId }, { replace: true });
    else setSearchParams({}, { replace: true });
    setPickerOpen(false);
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await patchExamination(id, {
        radiologist_notes: radNotes,
        final_diagnosis: finalDiag,
      });
      toast.success("Catatan tersimpan");
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const confirmAsCorrect = async () => {
    setVerifying(true);
    try {
      await verifyExamination(id, "confirmed", []);
      const updated = await getExamination(id);
      setExam(updated);
      toast.success("AI prediction confirmed sebagai ground truth");
    } catch (e) {
      toast.error("Gagal verifikasi");
    } finally {
      setVerifying(false);
    }
  };

  const saveCorrections = async () => {
    if (boxes.some((b) => !CLASSES.includes(b.class))) {
      toast.error("Ada box dengan class label tidak valid");
      return;
    }
    setVerifying(true);
    try {
      // Ensure all boxes are marked as radiologist-sourced after correction
      const cleaned = boxes.map((b) => ({
        class: b.class,
        confidence: 1.0,
        x: b.x,
        y: b.y,
        width: b.width,
        height: b.height,
        source: "radiologist",
      }));
      await verifyExamination(id, "corrected", cleaned);
      const updated = await getExamination(id);
      setExam(updated);
      setBoxes(cleaned);
      setEditMode(false);
      toast.success(`${cleaned.length} koreksi tersimpan sebagai ground truth`);
    } catch (e) {
      toast.error("Gagal menyimpan koreksi", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setVerifying(false);
    }
  };

  const cancelEdit = () => {
    // Reset boxes to saved state
    const initial =
      exam?.verification_status === "corrected" && exam.corrections?.length
        ? exam.corrections
        : (exam?.predictions || []).map((p) => ({ ...p, source: "ai" }));
    setBoxes(initial);
    setEditMode(false);
  };

  const remove = async () => {
    if (!window.confirm("Hapus pemeriksaan ini?")) return;
    try {
      await deleteExamination(id);
      toast.success("Terhapus");
      navigate("/history");
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const changeBoxClass = (i, newClass) => {
    const next = [...boxes];
    next[i] = { ...next[i], class: newClass };
    setBoxes(next);
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
      </div>
    );
  }
  if (!exam) {
    return (
      <div className="p-10 text-zinc-500">
        Pemeriksaan tidak ditemukan.{" "}
        <Link to="/history" className="text-indigo-400">
          Kembali
        </Link>
      </div>
    );
  }

  const status = exam.verification_status || "unreviewed";
  const statusBadge = {
    unreviewed: { text: "Belum diverifikasi", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
    confirmed: { text: "AI dikonfirmasi radiolog", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
    corrected: { text: "Dikoreksi radiolog", cls: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20" },
  }[status];

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <Link
            to="/history"
            data-testid="back-to-history"
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-3"
          >
            <ArrowLeft className="w-3 h-3" /> Riwayat
          </Link>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
              {exam.patient?.name || "Unnamed Patient"}
            </h1>
            {isAdmin && (
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${statusBadge.cls}`}
                data-testid="verification-status-badge"
              >
                {statusBadge.text}
              </span>
            )}
            {exam.report_standard === "ilo" && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded border bg-teal-500/10 text-teal-300 border-teal-500/20"
                data-testid="ilo-standard-badge"
              >
                <HardHat className="w-3 h-3" /> Standar ILO
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-500 font-mono">
            {exam.patient?.patient_id || "no-mr"} ·{" "}
            {new Date(exam.created_at).toLocaleString()} ·{" "}
            {exam.inference_time_ms ? `${Math.round(exam.inference_time_ms)} ms` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              setDownloadingPdf(true);
              try {
                await downloadPdf(id, { compareWith: compareId || null, includeReport: true });
              } catch (e) {
                toast.error("Gagal download PDF");
              } finally {
                setDownloadingPdf(false);
              }
            }}
            disabled={downloadingPdf}
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-colors"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF{compareExam ? " (+bandingan)" : ""}
          </button>
          <button
            onClick={remove}
            data-testid="delete-exam-detail-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 px-4 py-2 text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Hapus
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Patient info */}
        <aside className="xl:col-span-3">
          <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 space-y-3 text-sm">
            <InfoRow label="Umur" value={exam.patient?.age ?? "-"} />
            <InfoRow label="Gender" value={exam.patient?.gender || "-"} />
            <InfoRow label="Tanggal Exam" value={exam.patient?.exam_date || "-"} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-1">
                Catatan Klinis
              </div>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap">
                {exam.patient?.notes || "-"}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 rounded-xl border border-zinc-900 bg-zinc-900/40 p-4 text-xs space-y-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
              Legend
            </div>
            {CLASSES.map((c) => (
              <div key={c} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: CLASS_COLORS[c] }}
                />
                <span className="text-zinc-300">{c}</span>
              </div>
            ))}
            <div className="pt-2 mt-2 border-t border-zinc-800 space-y-1.5">
              <div className="flex items-center gap-2">
                <svg width="20" height="10">
                  <line x1="0" y1="5" x2="20" y2="5" stroke="#818cf8" strokeWidth="2" strokeDasharray="4 2" />
                </svg>
                <span className="text-zinc-400">AI prediction</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="20" height="10">
                  <line x1="0" y1="5" x2="20" y2="5" stroke="#818cf8" strokeWidth="2" />
                </svg>
                <span className="text-zinc-400">Radiologist verified</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Viewer */}
        <section className="xl:col-span-6 space-y-3">
          {/* Compare toolbar */}
          <ComparePicker
            currentExam={exam}
            priorList={priorList}
            compareId={compareId}
            compareExam={compareExam}
            compareLoading={compareLoading}
            pickerOpen={pickerOpen}
            setPickerOpen={setPickerOpen}
            onChange={changeCompare}
          />

          {compareExam ? (
            <SideBySide
              current={{ exam, boxes }}
              prior={compareExam}
              hovered={hovered}
              onHover={setHovered}
            />
          ) : (
            <InteractiveBoxEditor
              imageSrc={exam.image_base64}
              imageWidth={exam.image_width}
              imageHeight={exam.image_height}
              boxes={boxes}
              setBoxes={setBoxes}
              editable={editMode}
              hoveredIndex={hovered}
              onHover={setHovered}
              testId="exam-viewer"
            />
          )}

          {/* Verification actions — ADMIN ONLY */}
          {isAdmin && !editMode && status === "unreviewed" && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <BadgeInfo className="w-4 h-4 text-amber-400 mt-0.5" />
                <div className="text-xs text-amber-200/80 leading-relaxed flex-1">
                  <strong className="text-amber-300">Perlu verifikasi radiolog.</strong>{" "}
                  Konfirmasi kalau prediksi AI sudah benar, atau koreksi manual bila ada
                  temuan yang salah / terlewat. Data verified akan tersedia di halaman{" "}
                  <Link to="/export" className="underline">Export</Link> untuk retraining.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={confirmAsCorrect}
                  disabled={verifying}
                  data-testid="confirm-ai-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium transition-colors"
                >
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Konfirmasi AI Benar
                </button>
                <button
                  onClick={() => setEditMode(true)}
                  data-testid="edit-boxes-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 text-sm font-medium transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  Koreksi Manual
                </button>
              </div>
            </div>
          )}

          {/* Non-admin info banner removed per user request */}

          {isAdmin && !editMode && status !== "unreviewed" && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setEditMode(true)}
                data-testid="edit-boxes-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 px-4 py-2 text-sm font-medium transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit ulang koreksi
              </button>
              {exam.verified_at && (
                <span className="text-xs text-zinc-500">
                  Verified {new Date(exam.verified_at).toLocaleString()}
                  {exam.verified_by ? ` · ${exam.verified_by}` : ""}
                </span>
              )}
            </div>
          )}

          {isAdmin && editMode && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={saveCorrections}
                disabled={verifying}
                data-testid="save-corrections-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium transition-colors"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Simpan Koreksi ({boxes.length} box)
              </button>
              <button
                onClick={cancelEdit}
                data-testid="cancel-edit-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 px-4 py-2 text-sm font-medium transition-colors"
              >
                Batal
              </button>
            </div>
          )}
        </section>

        {/* Findings + Notes */}
        <aside className="xl:col-span-3 space-y-4">
          <div className="rounded-xl border border-zinc-900 bg-zinc-900/40">
            <div className="px-5 py-4 border-b border-zinc-900">
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                {editMode ? "Koreksi Radiolog" : status === "corrected" ? "Ground Truth" : "AI Findings"}
              </div>
              <div className="font-display text-lg font-medium text-zinc-100 mt-0.5">
                {boxes.length} box
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {boxes.length === 0 && (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="text-sm text-zinc-200 font-medium">
                    Tidak ada temuan
                  </div>
                  {editMode && (
                    <div className="text-xs text-zinc-500 mt-1">
                      Klik &quot;Add Finding&quot; lalu drag pada gambar untuk menambah box.
                    </div>
                  )}
                </div>
              )}
              <div className="divide-y divide-zinc-900">
                {boxes.map((b, i) => {
                  const conf = b.confidence ?? 1;
                  const color = CLASS_COLORS[b.class] || "#818cf8";
                  return (
                    <div
                      key={i}
                      data-testid={`box-item-${i}`}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      className={`px-5 py-3 transition-colors ${
                        hovered === i ? "bg-zinc-800/60" : "hover:bg-zinc-900/60"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span
                            className="w-1.5 h-6 rounded-sm shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          {editMode ? (
                            <select
                              data-testid={`box-class-select-${i}`}
                              value={b.class}
                              onChange={(e) => changeBoxClass(i, e.target.value)}
                              className="bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 px-2 py-1 flex-1 min-w-0"
                            >
                              {CLASSES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm font-medium text-zinc-100 truncate">
                              {b.class}
                            </span>
                          )}
                        </div>
                        <span
                          className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400"
                        >
                          {conf < 1 ? `${(conf * 100).toFixed(0)}%` : "★"}
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-600 ml-3.5">
                        {b.source === "radiologist" ? "radiologist" : "ai"} · x:{Math.round(b.x)} y:{Math.round(b.y)} · {Math.round(b.width)}×{Math.round(b.height)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Radiologist Notes
              </label>
              <textarea
                data-testid="exam-rad-notes"
                value={radNotes}
                onChange={(e) => setRadNotes(e.target.value)}
                rows={3}
                className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Final Diagnosis
              </label>
              <textarea
                data-testid="exam-final-diag"
                value={finalDiag}
                onChange={(e) => setFinalDiag(e.target.value)}
                rows={2}
                className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
              />
            </div>
            <button
              onClick={saveNotes}
              disabled={saving}
              data-testid="save-exam-notes-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2.5 text-sm font-medium transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Simpan Catatan
            </button>
          </div>
        </aside>
      </div>

      {/* ILO classification (pneumoconiosis standard) */}
      <div className="mt-6">
        {exam.report_standard === "ilo" ? (
          <ILOPanel exam={exam} onExamUpdate={reloadExam} />
        ) : (
          <button
            type="button"
            data-testid="enable-ilo-btn"
            onClick={async () => {
              try {
                await patchExamination(id, { report_standard: "ilo" });
                await reloadExam();
                toast.success("Standar ILO diaktifkan untuk pemeriksaan ini");
              } catch {
                toast.error("Gagal mengaktifkan standar ILO");
              }
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-teal-500/20 bg-teal-500/5 hover:bg-teal-500/10 text-teal-300 px-4 py-2 text-sm font-medium transition-colors"
          >
            <HardHat className="w-4 h-4" />
            Gunakan Standar ILO (Pneumokoniosis)
          </button>
        )}
      </div>

      {/* Report Draft — full width below */}
      <div className="mt-6">
        <ReportDraftPanel
          exam={exam}
          onExamUpdate={reloadExam}
          priorList={priorList}
          selectedPriorId={compareId}
          onOpenComparison={() => {
            // Scroll to top of viewer where inline compare is shown
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (!compareId && priorList[0]) changeCompare(priorList[0].id);
          }}
        />
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
        {label}
      </span>
      <span className="text-sm text-zinc-200">{value}</span>
    </div>
  );
}

/**
 * ComparePicker — compact toolbar above the viewer.
 * - If prior auto-detected: shows badge "Membandingkan dengan …" + [Ganti] + [X]
 * - If no prior: shows [+ Bandingkan dgn foto lain] button opening picker
 */
function ComparePicker({
  currentExam,
  priorList,
  compareId,
  compareExam,
  compareLoading,
  pickerOpen,
  setPickerOpen,
  onChange,
}) {
  return (
    <div className="relative">
      {compareId ? (
        <div
          className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="compare-active-bar"
        >
          <div className="flex items-center gap-2 text-xs min-w-0">
            <GitCompareArrows className="w-4 h-4 text-indigo-400 shrink-0" />
            <span className="text-zinc-400">Membandingkan dengan:</span>
            {compareLoading ? (
              <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />
            ) : compareExam ? (
              <span className="text-zinc-100 font-medium truncate">
                {compareExam.patient?.name || "Unnamed"} ·{" "}
                <span className="font-mono text-zinc-400">
                  {compareExam.patient?.patient_id || "-"}
                </span>{" "}
                ·{" "}
                {compareExam.patient?.exam_date ||
                  compareExam.created_at?.slice(0, 10)}
              </span>
            ) : (
              <span className="text-rose-400">tidak ditemukan</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPickerOpen((v) => !v)}
              data-testid="change-compare-btn"
              className="inline-flex items-center gap-1 rounded-md text-[11px] px-2 py-1 text-indigo-300 hover:bg-indigo-500/10"
            >
              Ganti <ChevronDown className="w-3 h-3" />
            </button>
            <button
              onClick={() => onChange("")}
              data-testid="close-compare-btn"
              className="p-1 rounded-md text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setPickerOpen((v) => !v)}
          data-testid="open-compare-picker-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 hover:border-indigo-500/40 hover:bg-zinc-900 text-zinc-200 px-3.5 py-2 text-xs font-medium transition-colors"
        >
          <GitCompareArrows className="w-3.5 h-3.5 text-indigo-400" />
          Bandingkan dengan foto lain
        </button>
      )}

      {pickerOpen && (
        <ComparePickerPopover
          currentExamId={currentExam?.id}
          priorList={priorList}
          onChange={onChange}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function ComparePickerPopover({ currentExamId, priorList, onChange, onClose }) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      listExaminations(q)
        .then((rows) => setAll(rows.filter((r) => r.id !== currentExamId)))
        .catch(() => setAll([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, currentExamId]);

  const priorIds = useMemo(() => new Set(priorList.map((p) => p.id)), [priorList]);

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        data-testid="compare-picker-popover"
        className="absolute right-0 top-full mt-2 w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl z-40 overflow-hidden"
      >
        <div className="p-3 border-b border-zinc-900 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama / patient ID / diagnosis…"
            data-testid="compare-picker-search"
            className="flex-1 bg-transparent text-sm text-zinc-100 placeholder:text-zinc-600 outline-none"
          />
          <button
            onClick={onClose}
            className="p-1 rounded text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Memuat…
            </div>
          ) : all.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              Tidak ada exam lain untuk dibandingkan
            </div>
          ) : (
            <div className="divide-y divide-zinc-900">
              {all.map((e) => {
                const findings = (e.predictions || []).filter(
                  (p) => p.class !== "normal"
                ).length;
                const isSamePatient = priorIds.has(e.id);
                return (
                  <button
                    key={e.id}
                    onClick={() => onChange(e.id)}
                    data-testid={`compare-pick-${e.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/60 text-left transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-100 truncate">
                          {e.patient?.name || "Unnamed Patient"}
                        </span>
                        {isSamePatient && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                            Pasien sama
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate">
                        {e.patient?.patient_id || "-"} ·{" "}
                        {e.patient?.exam_date || e.created_at?.slice(0, 10)}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded shrink-0 ${
                        findings > 0
                          ? "bg-rose-500/10 text-rose-400"
                          : "bg-emerald-500/10 text-emerald-400"
                      }`}
                    >
                      {findings > 0 ? `${findings} temuan` : "Normal"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * SideBySide — simple 2-image comparison inline (no modal).
 * Left = current, Right = prior. Both use InteractiveBoxEditor in read-only mode.
 */
function SideBySide({ current, prior, hovered, onHover }) {
  const priorBoxes = useMemo(() => {
    return prior.verification_status === "corrected" && prior.corrections?.length
      ? prior.corrections
      : prior.predictions || [];
  }, [prior]);

  return (
    <div
      data-testid="side-by-side-inline"
      className="grid grid-cols-1 md:grid-cols-2 gap-3"
    >
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-400 font-semibold">
              Pemeriksaan Saat Ini
            </div>
            <div className="text-xs text-zinc-400 font-mono mt-0.5">
              {current.exam.patient?.exam_date || current.exam.created_at?.slice(0, 10)} ·{" "}
              {current.boxes.length} box
            </div>
          </div>
        </div>
        <InteractiveBoxEditor
          imageSrc={current.exam.image_base64}
          imageWidth={current.exam.image_width}
          imageHeight={current.exam.image_height}
          boxes={current.boxes}
          setBoxes={() => {}}
          editable={false}
          hoveredIndex={hovered}
          onHover={onHover}
          testId="cmp-current-viewer"
        />
      </div>
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Pemeriksaan Sebelumnya
            </div>
            <div className="text-xs text-zinc-400 font-mono mt-0.5">
              {prior.patient?.exam_date || prior.created_at?.slice(0, 10)} ·{" "}
              {priorBoxes.length} box
            </div>
          </div>
        </div>
        <InteractiveBoxEditor
          imageSrc={prior.image_base64}
          imageWidth={prior.image_width}
          imageHeight={prior.image_height}
          boxes={priorBoxes}
          setBoxes={() => {}}
          editable={false}
          testId="cmp-prior-viewer"
        />
      </div>
    </div>
  );
}
